import { DisplayError } from '@cotera/client/app/components/app';
import { Center, Loading, Text } from '@cotera/client/app/components/ui';
import { useDuckDBQuery } from '@cotera/client/app/etc/data/duckdb';
import {
  useWarehouseConnection,
  useWarehouseDialect,
  useWarehouseDialectType,
} from '@cotera/client/app/hooks';
import { useFuzzySearch } from '@cotera/client/app/hooks/use-fuzzy-search';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@cotera/client/v0/components/ui/table';
import { From, InformationSchema, SQL, Ty } from '@cotera/era';
import { groupBy } from 'lodash';
import { ChevronRightIcon, Database } from 'lucide-react';
import { Suspense, useMemo, useState } from 'react';
import { ErrorBoundary } from 'react-error-boundary';
import { z } from 'zod/v3';
import { useAction } from '../actions/framework';
import { CloseTabAction } from '../actions/tabs/close-tab.action';
import { TabItem } from '../components/topbar/tab-item';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { H4, Small } from '../components/ui/typography';
import { Tab, useTabParams } from '../tab-manager';
import { NewDatasetAction } from './actions/new-dataset.action';
import { Dataset } from './dataset.edit';
import { useAppData } from '@cotera/client/app/stores/org';
import { MultiCombobox } from '../components/ui/combobox';
import { useTextFieldTracking } from '@cotera/client/app/etc';
import { isProduction } from '@cotera/client/app/etc';
import { CsvUploadAction } from './csv-upload/csv-upload.action';

const columnInfoSchema = z.object({
  table_name: z.string(),
  table_schema: z.string(),
  column_name: z.string(),
  data_type: z.string(),
  is_nullable: z.boolean(),
});

type ColumnInfo = z.infer<typeof columnInfoSchema>;

/**
 * Note
 *  - In the isNew case, tab param `name`, if specified, does not have to unique to the existing symbols in the "graph"
 *    because uniqueness is handled at at the save dataset action phase.
 */
export const NewDataset: React.FC = () => {
  const { startingEraQl, tabId, id, name } = useTabParams<{
    startingEraQl?: string;
    tabId: string;
    id: string;
    name?: string;
  }>();

  if (startingEraQl !== undefined) {
    return (
      <Dataset isNew={true} startingEraQl={startingEraQl} id={id} name={name} />
    );
  }

  return (
    <>
      <Tab tabId={tabId}>
        <TabItem
          tab={{
            id: tabId,
            title: 'New Dataset',
            showClose: true,
          }}
        />
      </Tab>
      <ErrorBoundary
        fallbackRender={({ error }) => <DisplayError error={error} />}
      >
        <Inner />
      </ErrorBoundary>
    </>
  );
};

const Inner: React.FC = () => {
  const newDataset = useAction(NewDatasetAction);
  const closeTab = useAction(CloseTabAction);
  const { tabId } = useTabParams();

  const dialectType = useWarehouseDialectType();

  const allSchemas = useSchemas();
  const [selectedSchemas, setSelectedSchemas] = useState<string[]>(allSchemas);
  const [search, setSearch] = useState<string>('');
  const uploadCsv = useAction(CsvUploadAction);

  /**
   * Supported for dev (postgres) & Production snowflake only.
   */
  const showCsvUploadButton =
    !isProduction() || (isProduction() && dialectType === 'SNOWFLAKE');

  return (
    <div className="flex flex-col items-center gap-4 px-4 sm:px-8 lg:px-16 py-6 sm:py-10 overflow-hidden">
      <div className="w-full flex flex-col gap-2 justify-between">
        <div className="flex flex-col sm:flex-row gap-2 sm:justify-between sm:items-center">
          <H4>Create a new dataset</H4>
          <div className="flex flex-wrap gap-2">
            <Button
              variant="outline"
              className="shrink-0"
              onClick={async () => {
                await newDataset.execute({
                  startingEraQl: '',
                });
                await closeTab.execute({
                  id: tabId!,
                });
              }}
            >
              Create dataset from query
            </Button>

            {showCsvUploadButton && (
              <Button variant="outline" onClick={() => uploadCsv.execute({})}>
                Create dataset from csv upload
              </Button>
            )}
          </div>
        </div>
        <Small>
          Select a table from a schema or create a custom dataset from an EraQL
          query
        </Small>
      </div>

      <Suspense
        fallback={
          <Center>
            <Text.Title subtitle="Searching for tables in your warehouse">
              Loading...
            </Text.Title>
            <Loading.Dots />
          </Center>
        }
      >
        <Controls
          options={allSchemas.map((schema) => ({
            value: schema,
            label: schema,
          }))}
          schemas={selectedSchemas}
          setSchemas={setSelectedSchemas}
          search={search}
          setSearch={setSearch}
        />
        <DatasetTable selectedSchemas={selectedSchemas} search={search} />
      </Suspense>
    </div>
  );
};

const Controls: React.FC<{
  schemas: string[];
  setSchemas: React.Dispatch<React.SetStateAction<string[]>>;
  search: string;
  setSearch: React.Dispatch<React.SetStateAction<string>>;
  options: { value: string; label: string }[];
}> = ({ schemas, setSchemas, search, setSearch, options }) => {
  const [newSchemas, setNewSchemas] = useState(schemas);
  const trackChange = useTextFieldTracking('Dataset Name Typed');

  return (
    <div className="flex flex-col sm:flex-row gap-2 w-full sm:justify-between">
      <div className="flex flex-row gap-2 w-full sm:w-auto">
        <Input
          className="shrink-0 w-full sm:w-md"
          placeholder="Search"
          value={search}
          onChange={(e) => {
            const newValue = e.target.value;
            setSearch(newValue);
            trackChange(newValue);
          }}
        />
      </div>

      <div className="flex flex-row gap-2 flex-wrap">
        <MultiCombobox
          items={options}
          value={newSchemas}
          onChange={(value) => setNewSchemas(value)}
          placeholder="Type or search..."
          searchPlaceholder="Search schemas..."
          allowCustomValues={true}
        />
        <Button className="px-1" onClick={() => setSchemas(newSchemas)}>
          Check schemas
          <Database className="shrink-0 size-4" />
        </Button>
      </div>
    </div>
  );
};

const DatasetTable: React.FC<{
  selectedSchemas: string[];
  search: string;
}> = ({ selectedSchemas, search }) => {
  const infoSchema = useDuckDBQuery({
    rel: InformationSchema({
      schemas: selectedSchemas,
      type: 'columns',
    }),
  });

  const columns = useMemo(
    () => infoSchema.data.data.toArray() as ColumnInfo[],
    [infoSchema]
  );

  const fuzzySearch = useFuzzySearch(columns, [
    'table_name',
    'table_schema',
    'column_name',
  ]);

  const filteredColumns = search.length > 0 ? fuzzySearch(search) : columns;

  const tables = Object.entries(
    groupBy(filteredColumns, (c) => `${c.table_schema}.${c.table_name}`)
  ).map(([key, columns]) => ({
    name: key.split('.')[1]!,
    schema: key.split('.')[0]!,
    columns,
  }));

  return (
    <div className="flex flex-col gap-2 border border-border rounded-sm w-full min-h-0 overflow-hidden">
      <Table className="w-full overflow-y-auto">
        <TableHeader className="sticky top-0 bg-background z-10 border-b border-border">
          <TableRow>
            <TableHead>Table</TableHead>
            <TableHead>Schema</TableHead>
            <TableHead className="text-right">Number of Columns</TableHead>
            <TableHead />
          </TableRow>
        </TableHeader>
        <TableBody>
          {tables.map((table) => (
            <Row key={table.name} table={table} />
          ))}
        </TableBody>
      </Table>
    </div>
  );
};

const Row: React.FC<{
  table: { columns: ColumnInfo[]; schema: string; name: string };
}> = ({ table }) => {
  const [expanded, setExpanded] = useState(false);
  const newDataset = useAction(NewDatasetAction);
  const closeTab = useAction(CloseTabAction);
  const { tabId } = useTabParams();
  const dialect = useWarehouseDialect();

  const handleSelectTable = async () => {
    const startingEraQl = From({
      name: table.name,
      schema: table.schema,
      attributes: Object.fromEntries(
        table.columns.map(({ column_name, data_type }) => [
          column_name,
          safelyMakeEraType(dialect, data_type) ?? Ty.ty('super'),
        ])
      ),
    }).toEraQL();

    await newDataset.execute({
      startingEraQl: startingEraQl,
    });
    await closeTab.execute({
      id: tabId!,
    });
  };

  return (
    <>
      <TableRow
        className="cursor-pointer"
        onClick={() => {
          setExpanded(!expanded);
        }}
      >
        <TableCell>{table.name}</TableCell>
        <TableCell>{table.schema}</TableCell>
        <TableCell className="text-right">{table.columns.length}</TableCell>
        <TableCell className="text-right justify-end flex">
          <Button
            variant="ghost"
            size="icon"
            onClick={async (e) => {
              e.stopPropagation();
              await handleSelectTable();
            }}
          >
            <ChevronRightIcon className="shrink-0 size-4" />
          </Button>
        </TableCell>
      </TableRow>
      {expanded && (
        <TableRow>
          <TableCell colSpan={3}>
            <Table>
              <TableHeader>
                <TableHead>Column</TableHead>
                <TableHead>Type</TableHead>
                <TableHead>Era Type</TableHead>
                <TableHead>Nullable</TableHead>
              </TableHeader>
              <TableBody>
                {table.columns.map((column) => (
                  <TableRow key={column.column_name}>
                    <TableCell>{column.column_name}</TableCell>
                    <TableCell>{column.data_type}</TableCell>
                    <TableCell>
                      {eraType(dialect, column.data_type) ?? ''}
                    </TableCell>
                    <TableCell>{column.is_nullable ? 'Yes' : 'No'}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableCell>
        </TableRow>
      )}
    </>
  );
};

const eraType = (dialect: SQL.SqlDialect, type: string): string => {
  const eraTy = safelyMakeEraType(dialect, type);
  return eraTy ? Ty.displayTy(eraTy) : 'unknown';
};

const safelyMakeEraType = (dialect: SQL.SqlDialect, type: string) => {
  try {
    return dialect.tryWarehouseTypeToEraType(type);
  } catch {
    return null;
  }
};

/**
 * Returns the db "schemas" (eg. ['cotera_data'])
 */
export const useSchemas = (): string[] => {
  // manifest schemas are deprecated
  const manifestSchemas = useAppData((c) => c.manifestMeta.schemas).read;

  // the normal way to get schemas
  const warehouseSchemas = useWarehouseConnection();

  if (manifestSchemas.length > 0) {
    return manifestSchemas;
  }

  if (warehouseSchemas.data) {
    return [warehouseSchemas.data.readSchema];
  }

  return ['COTERA_DATA', 'cotera_data'];
};
